import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { Marquee, DimTile, ClassBand, ImpactItem, UCCard, TechCard } from '../components/UI';
import { DIMENSIONS, CAPABILITIES, TECH_FEATURES, MARQUEE_ITEMS } from '../data/content';
import styles from './Home.module.css';

const PROBLEMS = [
  //{ ico:'📄', title:'Not AI-Ready', desc:'Enterprise documents are written for humans. Ambiguous language and poor formatting confuse language models during retrieval and generation.' },
  { ico:'/images/icons/problem/not-ai-ready.svg', title:'Not AI-Ready', desc:'Enterprise documents are written for humans. Ambiguous language and poor formatting confuse language models during retrieval and generation.' },
  { ico:'/images/icons/problem/high-hallucination-risk.svg', title:'High Hallucination Risk', desc:'Incomplete, inconsistent content amplifies hallucinations in RAG pipelines, copilots, and semantic search — silently eroding trust.' },
  { ico:'/images/icons/problem/reviews-dont-scale.svg', title:"Reviews Don't Scale", desc:'Manual document reviews are slow, subjective, and impossible to standardise across large content libraries.' },
];

const SOLUTION_CARDS = [
  { ico:'/images/icons/solution/quantify-quality.svg', title:'Quantify Quality', desc:'Generates a precise 0–100 AI Friendliness Score across 12 dimensions for every document in your library.', tags:[{l:'0–100 Score'},{l:'12 Dimensions'}] },
  { ico:'/images/icons/solution/enforce-compliance.svg', title:'Enforce Compliance', desc:'Validates every document against your internal policies with rule-level PASS / PARTIAL / FAIL across 24 configurable rules.', tags:[{l:'PASS',c:'g'},{l:'PARTIAL',c:'a'},{l:'FAIL',c:'r'}] },
  { ico:'/images/icons/solution/auto-improve-content.svg', title:'Auto-Improve Content', desc:'AI Recommended Draft Generator rewrites entire documents with improvements applied inline — at enterprise scale.', tags:[{l:'Inline Rewrites',c:'g'},{l:'Enterprise Scale'}] },
];

const IMPACTS = [
  { icon:'/images/icons/business-impact/productivity.svg', num:'70–80%', label:'Productivity',  desc:'Reduction in manual review effort' },
  { icon:'/images/icons/business-impact/reliability.svg', num:'↑ AI',   label:'Reliability',   desc:'Improved GenAI accuracy' },
  { icon:'/images/icons/business-impact/standardised.svg', num:'100%',   label:'Standardised',  desc:'Consistent quality benchmark' },
  { icon:'/images/icons/business-impact/compliance.svg', num:'Auto',   label:'Compliance',    desc:'Automated policy enforcement' },
  { icon:'/images/icons/business-impact/generic-and-measurable.svg', num:'ROI',  label:'Generic and Measurable', desc:'Annual savings at enterprise scale' },
];

const USE_CASES = [
  { icon:'/images/icons/usecases/ai-readyknowledge-bases.svg', title:'AI-Ready Knowledge Bases',        desc:'Transform repositories into structured, AI-consumable assets ready for RAG and semantic search.' },
  { icon:'/images/icons/usecases/sops-policies-governance.svg', title:'SOPs, Policies & Governance',     desc:'Ensure operational documents meet quality and compliance standards before they power workflows.' },
  { icon:'/images/icons/usecases/customer-support-content.svg', title:'Customer Support Content',        desc:'Optimise support articles so AI copilots deliver accurate, confident answers every time.' },
  { icon:'/images/icons/usecases/legal-compliance-docs.svg', title:'Legal & Compliance Docs',        desc:'Validate legal documents against frameworks with automated rule-level PASS / PARTIAL / FAIL scoring.' },
];

const CTA_PILLS = [
  { icon:'/images/icons/why-readiq/deploy-gen-ai-safely.svg',       label:'Deploy GenAI safely' },
  { icon:'/images/icons/why-readiq/scale-knowledge-efficiently.svg',    label:'Scale knowledge efficiently' },
  { icon:'/images/icons/why-readiq/convert-docs-into-ai-assets.svg',       label:'Convert docs into AI assets' },
  { icon:'/images/icons/why-readiq/enforce-governance-automatically.svg', label:'Enforce governance automatically' },
  { icon:'/images/icons/why-readiq/measure-quality-objectively.svg',    label:'Measure quality objectively' },
  { icon:'/images/icons/why-readiq/enable-continuous-improvement.svg', label:'Enable continuous improvement' },
];


export default function Home() {
  return (
    <>
      <SEO
        title="ReadIQ — GenAI-Ready Document Intelligence"
        description="ReadIQ evaluates, scores, and auto-improves enterprise documents for GenAI readiness. 12-dimension AI Friendliness Score, auto-remediation, and org-rule compliance in one platform."
        canonical="/"
        keywords="GenAI document readiness, AI document scoring, document quality platform, ReadIQ, enterprise AI readiness"
      />

      {/* HERO */}
      <section className={styles.hero} aria-labelledby="hero-h1">
        <div className={`wrap ${styles.heroInner}`}>
          <div className={styles.heroLeft}>
            <div className={styles.heroTag}>
              <span className={styles.heroTagDot} aria-hidden="true" />
              <span>AI-Powered Enterprise Platform</span>
            </div>
            <h1 id="hero-h1" className={styles.heroH1}>
              Your documents,<br/><em>GenAI-ready</em>
            </h1>
            <p className={styles.heroDesc}>
              ReadIQ evaluates, improves, and operationalises enterprise documents into high-quality,
              AI-consumable knowledge assets — with measurable scores, compliance checks, and automated improvements.
            </p>
            <div className={styles.heroBtns}>
              <Link to="/request-demo" className="btn btn-lime" style={{padding:'12px 26px',fontSize:'14.5px'}}>Request a Demo →</Link>
              <Link to="/capabilities" className="btn btn-outline" style={{color:'rgba(255,255,255,.75)',borderColor:'rgba(255,255,255,.2)'}}>Explore Capabilities</Link>
            </div>
            <div className={styles.heroStats}>
              {[['12','Quality Dimensions'],['0–100','AI Friendliness Score'],['70–80%','Effort Reduction']].map(([n,l])=>(
                <div className={styles.hstat} key={l}>
                  <div className={styles.hstatNum}>{n}</div>
                  <div className={styles.hstatLbl}>{l}</div>
                </div>
              ))}
            </div>
            <div className={styles.shortcuts}>
              {[['📊','Evaluation','/evaluation'],['⚙️','Capabilities','/capabilities'],['🔁','Workflow','/workflow'],['📈','Analytics','/analytics']].map(([ico,lbl,to])=>(
                <Link key={to} to={to} className={styles.hsLink}>
                  <span>{ico} {lbl}</span><span style={{opacity:.4}}>→</span>
                </Link>
              ))}
            </div>
          </div>

          {/* Hero dashboard panel */}
          <div className={styles.heroPanel} role="img" aria-label="ReadIQ live score dashboard showing 84 GenAI-Ready">
            <div className={styles.topbar}>
              <div className={styles.dot} style={{background:'#ff5f57'}}/>
              <div className={styles.dot} style={{background:'#febc2e'}}/>
              <div className={styles.dot} style={{background:'#28c840'}}/>
              <span className={styles.topbarTitle}>ReadIQ — Document Evaluation</span>
            </div>
            <div className={styles.hpHead}>
              <div>
                <div className={styles.hpLabel}>AI Friendliness Score</div>
                <div className={styles.hpScore}>84</div>
                <div className={styles.hpSub}>Information Intelligence Index — Active</div>
              </div>
              <div className={styles.hpPill}>GenAI-Ready</div>
            </div>
            <div className={styles.hpBody}>
              {[['Structure & Organization',91,'var(--blue)'],['Clarity & Precision',87,'var(--blue)'],['Completeness',76,'var(--amber)'],['Hallucination Risk',89,'var(--green)'],['Regulatory Compliance',68,'var(--red)'],['Metadata & Discovery',82,'var(--blue)']].map(([lbl,val,c])=>(
                <div className={styles.hpRow} key={lbl}>
                  <div className={styles.hpLbl}>{lbl}</div>
                  <div className={styles.hpTrack}><div className={styles.hpFill} style={{width:`${val}%`,background:c}}/></div>
                  <div className={styles.hpVal}>{val}</div>
                </div>
              ))}
            </div>
            <div className={styles.hpFoot}>
              <span className={styles.hfPass}>GDPR — PASS</span>
              <span className={styles.hfPart}>ISO 27001 — PARTIAL</span>
              <span className={styles.hfFail}>Data Retention — FAIL</span>
            </div>
          </div>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      {/* PROBLEM */}
      <section className="section section-alt">
        <div className="wrap">
          <div style={{marginBottom:40}}>
            <div className="eyebrow">The Problem</div>
            <h2 className="sh">Enterprise documents aren't built for AI</h2>
            <p className="sub" style={{marginBottom:0}}>Poor structure, ambiguity, and inconsistency silently degrade AI performance — causing hallucinations, inaccurate outputs, and broken knowledge retrieval.</p>
          </div>
          <div className={styles.probGrid}>
            {PROBLEMS.map(p=>(
              <div key={p.title} className={styles.probCard}>
                {/*<div className={styles.probIco}>{p.ico}</div> */}
                <div className={styles.probIco}>
                  <img src={p.ico} alt={p.title} width={24} height={24} />
                </div>
                <h3>{p.title}</h3><p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTION */}
      <section className="section">
        <div className="wrap">
          <div className="eyebrow">The Solution</div>
          <h2 className="sh">A standardised AI-driven evaluation layer</h2>
          <p className="sub">ReadIQ quantifies document quality, identifies gaps across 12 dimensions, auto-improves content, and enforces organisation-specific rules.</p>
          <div className={styles.capsGrid}>
            {SOLUTION_CARDS.map(c=>(
              <div key={c.title} className="card">
                <div className="card-ico"><img src={c.ico} alt={c.title} width={24} height={24} /></div>
                <h3>{c.title}</h3><p>{c.desc}</p>
                <div className="tags">{c.tags.map((t,i)=><span key={i} className={`tag ${t.c||''}`}>{t.l}</span>)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DIMENSIONS */}
      <section className="section section-dark">
        <div className="wrap">
          <div className="eyebrow light">Quality Framework</div>
          <h2 className="sh light">12 dimensions. One unified score.</h2>
          <p className="sub light">Every aspect of document quality, quantified and traceable.</p>
          <div className={styles.dimsGrid}>
            {DIMENSIONS.map((d,i)=><DimTile key={d} n={String(i+1).padStart(2,'0')} label={d}/>)}
          </div>
          <div className={styles.classGrid}>
            <ClassBand score="≥ 90" name="GenAI-Native"    desc="Production-ready for AI"        variant="n"/>
            <ClassBand score="≥ 75" name="GenAI-Ready"     desc="Minor gaps remain"               variant="r"/>
            <ClassBand score="< 75" name="Needs Improvement" desc="Significant issues"             variant="i"/>
            <ClassBand score="Unsafe" name="Unsafe"         desc="Do not use with AI"              variant="u"/>
          </div>
        </div>
      </section>

      {/* CORE CAPABILITIES */}
      <section className="section">
        <div className="wrap">
          <div>
            <div className="eyebrow">Core Capabilities</div>
            <h2 className="sh">Six pillars of document intelligence</h2>
            <p className="sub">Every feature purpose-built to bridge the gap between human-authored content and AI-ready knowledge assets.</p>
          </div>
          <div className={styles.capsGrid}>
            {CAPABILITIES.map(c=>(
              <div key={c.num} className="card">
                <div className="card-ico">
                  <img src={c.icon} alt={c.title} width={24} height={24} />
                </div>
                <h3>{c.title}</h3><p>{c.desc}</p>
                <div className="tags">{c.tags.map((t,i)=><span key={i} className={`tag ${t.c||''}`}>{t.l}</span>)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TECH FEATURES */}
      <section className="section section-dark">
        <div className="wrap">
          <div className="eyebrow light">Technology</div>
          <h2 className="sh light">Enterprise-grade technology features</h2>
          <p className="sub light">Built for scale, security, and seamless integration into your existing infrastructure.</p>
          <div className={styles.techGrid}>
            {TECH_FEATURES.map(t=><TechCard key={t.title} {...t}/>)}
          </div>
        </div>
      </section>

      {/* WORKFLOW OVERVIEW */}
      <section className="section section-alt">
        <div className="wrap">
          <div>
            <div className="eyebrow">Workflow & Governance</div>
            <h2 className="sh">End-to-end document lifecycle</h2>
            <p className="sub">Every document follows a governed, auditable path from first draft to approved AI-ready knowledge asset.</p>
          </div>
          <div className={styles.stepsRow}>
            {[['/images/icons/workflow/draft.svg','Draft','Author creates content'],['/images/icons/workflow/submit.svg','Submit','Sent for AI evaluation'],['/images/icons/workflow/evaluate.svg','Evaluate','Scored across 12 dims'],['/images/icons/workflow/review.svg','Review','Human reviewer inspects'],['/images/icons/workflow/approve.svg','Approve','Published as AI-ready asset']].map(([ico,name,desc])=>(
              <div key={name} className={styles.stepCol}>
                <div className={styles.stepBbl}>
                  <img src={ico} width={24} height={24} />
                </div>
                <div className={styles.stepName}>{name}</div>
                <div className={styles.stepDesc}>{desc}</div>
              </div>
            ))}
          </div>
          {/*<div className={styles.roles}>
            <div className={styles.rolesLabel}>Role-Based Access Control</div>
            <div className={styles.rolesRow}>
              {[['#7c3aed','Admin'],['var(--blue)','Evaluator'],['var(--green)','Reviewer'],['var(--amber)','Approver'],['var(--muted)','Viewer']].map(([c,n])=>(
                <div key={n} className={styles.roleChip}><span className={styles.rdot} style={{background:c}}/> {n}</div>
              ))}
            </div>
          </div> */}
        </div>
      </section>

      {/* IMPACT */}
      <section className="section">
        <div className="wrap">
          <div>
            <div className="eyebrow">Business Impact</div>
            <h2 className="sh">Measurable outcomes from day one</h2>
            <p className="sub">Quantifiable returns across productivity, AI reliability, governance, and continuous improvement.</p>
          </div>
          <div className={styles.impactStrip}>
            {IMPACTS.map(i=><ImpactItem key={i.label} {...i}/>)}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="section section-alt">
        <div className="wrap">
          <div>
            <div className="eyebrow">Key Use Cases</div>
            <h2 className="sh">Built for knowledge-intensive teams</h2>
            <p className="sub">From legal briefs to support articles — ReadIQ ensures every document performs at its best inside AI workflows.</p>
          </div>
          <div className={styles.ucGrid}>
            {USE_CASES.map(u=><UCCard key={u.title} icon={u.icon} title={u.title} desc={u.desc}/>)}
            <UCCard icon="/images/icons/usecases/enterprise-ai-copilots-search-systems.svg" title="Enterprise AI Copilots & Search Systems" desc="Feed high-quality, evaluated documents into enterprise RAG pipelines and semantic search — reducing hallucination risk at the source and ensuring precision at scale." span/>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={styles.ctaSection}>
        <div className="wrap">
          <div className="eyebrow light" style={{justifyContent:'center'}}>Why ReadIQ</div>
          <h2>Bridge the gap between<br/>human content and AI consumption</h2>
          <p>ReadIQ enables organisations to deploy GenAI safely, scale knowledge systems efficiently, and convert documents into intelligent, self-optimising assets.</p>
          <div className={styles.ctaPills}>
            {CTA_PILLS.map(p => (
              <div key={p.label} className={styles.ctaPill}>
                <img src={p.icon} alt="" width={16} height={16} aria-hidden="true" />
                {p.label}
              </div>
            ))}
          </div>
          <div className={styles.ctaBtns}>
            <Link to="/request-demo" className="btn btn-lime" style={{padding:'13px 32px',fontSize:'15px'}}>Request a Demo →</Link>
            <Link to="/capabilities" className="btn btn-white" style={{padding:'13px 24px',fontSize:'15px'}}>See All Features</Link>
          </div>
        </div>
      </section>
    </>
  );
}
